import api from './api';

// File size limits (in bytes)
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;  // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;   // 50MB
const MAX_VOICE_SIZE = 5 * 1024 * 1024;    // 5MB

const API_URL = process.env.REACT_APP_API_URL || '/api';
const getApiUrl = () => {
  const url = process.env.REACT_APP_API_URL || '/api';
  if (url.startsWith('http')) {
    return url.includes('/api') ? url : `${url}/api`;
  }
  return url;
};

// Upload using XMLHttpRequest (more reliable for file uploads)
const uploadWithXHR = (url, formData) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${getApiUrl()}${url}`);

    const token = localStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    // Let browser set Content-Type with boundary

    xhr.withCredentials = true;

    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          reject(new Error(data.error || 'Upload failed'));
        }
      } catch (e) {
        reject(new Error('Invalid response'));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error'));
    };

    xhr.send(formData);
  });
};

// Validate file before upload
const validateMediaFile = (file) => {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');

  if (isImage && file.size > MAX_IMAGE_SIZE) {
    throw new Error('Image file too large. Maximum size is 10MB');
  }
  if (isVideo && file.size > MAX_VIDEO_SIZE) {
    throw new Error('Video file too large. Maximum size is 50MB');
  }
  if (!isImage && !isVideo) {
    throw new Error('Unsupported file type. Only images and videos are allowed');
  }
};

const validateVoiceFile = (file) => {
  if (file.size > MAX_VOICE_SIZE) {
    throw new Error('Voice file too large. Maximum size is 5MB');
  }
};

export const chatApi = {
  // Conversations
  getConversations: () => api.get('/conversations'),
  getConversation: (id) => api.get(`/conversations/${id}`),
  createConversation: (data) => api.post('/conversations', data),

  // Messages
  getMessages: (conversationId, limit = 50, offset = 0) =>
    api.get(`/conversations/${conversationId}/messages?limit=${limit}&offset=${offset}`),
  getMessageCount: (conversationId) =>
    api.get(`/conversations/${conversationId}/messages/count`),
  getMessagesBefore: (conversationId, beforeTime, limit = 50) =>
    api.get(`/conversations/${conversationId}/messages/before?before=${encodeURIComponent(beforeTime)}&limit=${limit}`),
  getMessagesAfter: (conversationId, afterTime, limit = 50) =>
    api.get(`/conversations/${conversationId}/messages/after?after=${encodeURIComponent(afterTime)}&limit=${limit}`),
  getMessagesBetween: (conversationId, afterTime, beforeTime, limit = 50) =>
    api.get(`/conversations/${conversationId}/messages/between?after=${encodeURIComponent(afterTime)}&before=${encodeURIComponent(beforeTime)}&limit=${limit}`),
  searchMessages: (conversationId, query, limit = 50, offset = 0) =>
    api.get(`/conversations/${conversationId}/messages/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`),
  sendMessage: (data) => api.post('/messages', data),
  updateMessage: (id, content) => api.put(`/messages/${id}`, { content }),
  deleteMessage: (id) => api.delete(`/messages/${id}`),

  // Media uploads
  uploadMessageMedia: async (messageId, file) => {
    validateMediaFile(file);
    const formData = new FormData();
    formData.append('media', file);
    // Use fetch to bypass axios issues
    return await uploadWithXHR(`/messages/${messageId}/media`, formData);
  },
  uploadMessageVoice: async (messageId, audioBlob) => {
    validateVoiceFile(audioBlob);
    const formData = new FormData();
    formData.append('voice', audioBlob, 'voice.webm');
    // Use fetch to bypass axios issues
    return await uploadWithXHR(`/messages/${messageId}/voice`, formData);
  },

  // Reactions
  addReaction: (messageId, reaction) =>
    api.post(`/messages/${messageId}/reactions`, { reaction }),
  removeReaction: (messageId, reaction) =>
    api.delete(`/messages/${messageId}/reactions/${reaction}`),

  // Get single message by ID
  getMessage: (messageId) =>
    api.get(`/messages/${messageId}`),

  // Create short link for a message
  createMessageLink: (messageId) =>
    api.post(`/messages/${messageId}/link`),

  // Resolve short link to message
  resolveMessageLink: (shortCode) =>
    api.get(`/links/${shortCode}`),

  // Cross-chat message search
  searchAllMessages: (query, limit = 50, offset = 0) =>
    api.get(`/messages/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`),

  // Get messages around a specific message (for jumping to a message)
  getMessagesAround: (conversationId, messageId, beforeCount = 20, afterCount = 20) =>
    api.get(`/conversations/${conversationId}/messages/${messageId}/around?before=${beforeCount}&after=${afterCount}`),

  // Mark messages as read
  markMessagesAsRead: (conversationId) =>
    api.post(`/conversations/${conversationId}/read`),

  // Group chat - Add participants
  addParticipants: (conversationId, userIds) =>
    api.post(`/conversations/${conversationId}/participants`, { user_ids: userIds }),

  // Group chat - Remove participant
  removeParticipant: (conversationId, userId) =>
    api.delete(`/conversations/${conversationId}/participants/${userId}`),

  // Group chat - Transfer ownership
  transferOwnership: (conversationId, newOwnerId) =>
    api.put(`/conversations/${conversationId}/transferOwnership`, { new_owner_id: newOwnerId }),
};
